import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useSxTokens } from '../SessionCreateModal/ui/sxTokens';

import { staffApi } from 'sectors/pilates/staff/api/staffApi';
import { sessionsApi } from 'sectors/pilates/sessions/api/sessionsApi';
import { membersApi } from 'sectors/pilates/members/api/membersApi';

import { SessionKindModal } from '../SessionCreateModal/kinds/SessionKindModal';
import { SessionCreatePackageModal } from '../SessionCreateModal/kinds/SessionCreatePackageModal';
import { SessionCreatePaidModal } from '../SessionCreateModal/kinds/SessionCreatePaidModal';
import { SessionCreateFreeModal } from '../SessionCreateModal/kinds/SessionCreateFreeModal';
import { ConfirmDialog } from '../parts/ConfirmDialog';

function toEditDraft(detail) {
  if (!detail) return null;

  const billingMode = detail?.billingMode || 'PACKAGE';

  return {
    sessionId: detail.id,

    dayISO: detail.dayISO,
    startMin: detail.startMin,
    durationMin: detail.durationMin ?? 60,

    trainerId: detail.trainerId,
    trainerName: detail.trainerName,

    activityId: detail.activityId ?? null,
    activityName: detail.activityName ?? 'Pilates',

    capacity: detail.capacity ?? 1,
    billingMode,
    sessionType: detail.sessionType ?? 'NORMAL',
    note: detail.note ?? null,

    // ✅ backend detail response: participants already create-shape
    participants: Array.isArray(detail.participants) ? detail.participants : [],
  };
}

export function SessionEditModal({ open, onClose, session, onUpdated }) {
  const theme = useMuiTheme();
  const tokens = useSxTokens(theme);

  const [postWarnOpen, setPostWarnOpen] = useState(false);
  const [postWarnMsg, setPostWarnMsg] = useState('');

  const toWarnText = (resp) => {
    const ws = resp?.warnings;
    const arr = Array.isArray(ws) ? ws : [];
    if (!arr.length) return '';

    const lines = arr
      .map((w) => {
        if (!w) return null;
        if (typeof w === 'string') return `• ${w}`;
        return `• ${w.message || w.msg || JSON.stringify(w)}`;
      })
      .filter(Boolean);

    return lines.length ? ['Uyarılar:', ...lines].join('\n') : '';
  };

  const [trainers, setTrainers] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffError, setStaffError] = useState(null);

  useEffect(() => {
    if (!open) return;

    let alive = true;
    setStaffLoading(true);
    setStaffError(null);

    staffApi
      .trainerPicker()
      .then((items) => {
        if (!alive) return;

        const mapped = (Array.isArray(items) ? items : [])
          .filter((x) => x && x.id != null)
          .map((x) => ({
            id: x.id,
            name: String(x.fullName || '—').trim() || '—',
          }));

        setTrainers(mapped);
      })
      .catch((e) => {
        if (!alive) return;
        console.error('[SessionEditModal] trainerPicker failed:', e);
        setTrainers([]);
        setStaffError(e);
      })
      .finally(() => {
        if (!alive) return;
        setStaffLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [open]);

  const loadMemberPackages = useCallback(async (memberId) => {
    const res = await membersApi.memberPackages(memberId);
    const data = res?.data ?? res;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.content)) return data.content;
    return [];
  }, []);

  // ✅ detail
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setDetail(null);
      setDetailLoading(false);
      return;
    }

    const id = session?.id;
    if (!id) {
      setDetail(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setDetailLoading(true);
      try {
        const res = await sessionsApi.getSession(id);
        const data = res?.data ?? res;
        if (!cancelled) setDetail(data);
      } catch (e) {
        console.error('[SessionEditModal] getSession failed:', e);
        if (!cancelled) setDetail(null);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, session?.id]);

  // modal routing
  const [kindOpen, setKindOpen] = useState(false);
  const [kind, setKind] = useState(null);

  const [pkgOpen, setPkgOpen] = useState(false);
  const [paidOpen, setPaidOpen] = useState(false);
  const [freeOpen, setFreeOpen] = useState(false);

  const draft = useMemo(() => toEditDraft(detail), [detail]);

  useEffect(() => {
    if (!open) {
      setKindOpen(false);
      setPkgOpen(false);
      setPaidOpen(false);
      setFreeOpen(false);
      setKind(null);
      return;
    }

    const bm = String(detail?.billingMode || '').toUpperCase();

    if (bm === 'PAID') {
      setPaidOpen(true);
      setPkgOpen(false);
      setFreeOpen(false);
      setKindOpen(false);
      setKind('PAID');
      return;
    }
    if (bm === 'FREE') {
      setFreeOpen(true);
      setPkgOpen(false);
      setPaidOpen(false);
      setKindOpen(false);
      setKind('FREE');
      return;
    }

    // default PACKAGE
    setPkgOpen(true);
    setPaidOpen(false);
    setFreeOpen(false);
    setKindOpen(false);
    setKind('PACKAGE');
  }, [open, detail?.billingMode]);

  const closeAll = () => {
    setKindOpen(false);
    setPkgOpen(false);
    setPaidOpen(false);
    setFreeOpen(false);
    setKind(null);
    setDetail(null);
    setDetailLoading(false);
    onClose?.();
  };

  const backToKind = () => {
    setPkgOpen(false);
    setPaidOpen(false);
    setFreeOpen(false);
    setKindOpen(true);
  };

  const pickKind = (k) => {
    setKind(k);
    setKindOpen(false);
    if (k === 'PACKAGE') setPkgOpen(true);
    if (k === 'PAID') setPaidOpen(true);
    if (k === 'FREE') setFreeOpen(true);
  };

  const handleUpdate = useCallback(
    async (payload) => {
      const id = payload?.sessionId ?? detail?.id ?? session?.id;
      if (!id) throw new Error('sessionId missing');

      const t = (trainers || []).find((x) => String(x.id) === String(payload?.trainerId));
      const trainerName = String(payload?.trainerName || t?.name || '').trim();

      const fixed = { ...payload, trainerName };

      const res = await sessionsApi.updateSession(id, fixed);
      const data = res?.data ?? res;

      const warnText = toWarnText(data);
      if (warnText) {
        setPostWarnMsg(warnText);
        setPostWarnOpen(true);
      }

      return data;
    },
    [detail?.id, session?.id, trainers]
  );

  const common = {
    onClose: closeAll,
    onBack: backToKind,
    onCreate: handleUpdate,

    tokens,
    trainers,
    staffLoading: staffLoading || detailLoading,
    staffError,

    dayISO: draft?.dayISO,
    startMin: draft?.startMin,
    defaultTrainerId: draft?.trainerId ?? 'ALL',
    initialDraft: draft,
  };

  return (
    <>
      <SessionKindModal open={kindOpen} onClose={closeAll} onPick={pickKind} tokens={tokens} />

      <SessionCreatePackageModal
        open={pkgOpen}
        {...common}
        loadMemberPackages={loadMemberPackages}
        onCreated={async () => {
          await onUpdated?.();
        }}
      />

      <SessionCreatePaidModal
        open={paidOpen}
        {...common}
        onCreated={async () => {
          await onUpdated?.();
        }}
      />

      <SessionCreateFreeModal
        open={freeOpen}
        {...common}
        onCreated={async () => {
          await onUpdated?.();
        }}
      />
      <ConfirmDialog
        open={postWarnOpen}
        tone="warning"
        title="Uyarı"
        subtitle="Seans güncellendi"
        message={postWarnMsg}
        confirmText="Tamam"
        hideCancel
        onConfirm={() => setPostWarnOpen(false)}
        onCancel={() => setPostWarnOpen(false)}
      />
    </>
  );
}
